/* ============================================================
   Startup Rev — Cart interactions
   Self-contained: handles +/- quantity, remove with collapse
   animation, optimistic updating, and live nav-badge sync.
   Does not depend on Dawn's cart.js.
   ============================================================ */

(() => {
  if (window.__srCartScriptLoaded) return;
  window.__srCartScriptLoaded = true;

  const ITEMS_SECTION_ID = 'cart-items';
  const FOOTER_SECTION_ID = 'cart-footer';

  const getAjaxUrl = (routeName, fallback) => {
    const route = (window.routes && window.routes[routeName]) || fallback;
    return `${route}.js`;
  };

  const formatMoney = (cents, currencyFormat) => {
    const amount = (Number(cents) / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (currencyFormat) {
      return currencyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }
    return amount;
  };

  const updateNavBadge = (cart) => {
    if (!cart || typeof cart.item_count === 'undefined') return;
    const count = Number(cart.item_count) || 0;
    const label = count > 0 ? 'Current Passes' : 'Get Your Pass';

    document.querySelectorAll('[data-sr-cart-label]').forEach((node) => {
      node.textContent = label;
    });

    document.querySelectorAll('[data-sr-cart-count]').forEach((node) => {
      node.textContent = String(count);
      node.classList.toggle('is-empty', count === 0);
      node.setAttribute('aria-label', `${count} items in cart`);
    });
  };

  const updateCartStatus = (cart) => {
    if (!cart || typeof cart.item_count === 'undefined') return;
    const root = document.querySelector('[data-sr-cart]');
    const count = Number(cart.item_count) || 0;
    const countText = root && root.querySelector('[data-sr-cart-count-text]');
    const countLabel = root && root.querySelector('[data-sr-cart-count-label]');
    const subtotal = root && root.querySelector('[data-sr-cart-subtotal]');

    if (countText) countText.textContent = String(count);
    if (countLabel) countLabel.textContent = `\u00a0${count === 1 ? 'pass' : 'passes'}`;
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price, root.dataset.srCartMoneyFormat);
  };

  const setItemUpdating = (line, on) => {
    const card = document.querySelector(`[data-sr-cart-line="${line}"]`);
    if (card) card.classList.toggle('is-updating', !!on);
  };

  const replaceSection = (sectionId, html) => {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const targets = sectionId === ITEMS_SECTION_ID
      ? ['#main-cart-items .js-contents', '#main-cart-items']
      : ['#main-cart-footer .js-contents', '#main-cart-footer'];

    for (const selector of targets) {
      const incoming = parsed.querySelector(selector);
      const current = document.querySelector(selector);
      if (incoming && current) {
        current.innerHTML = incoming.innerHTML;
        if (sectionId === ITEMS_SECTION_ID) {
          const incomingDiscounts = parsed.querySelector('#sr-cart-discounts-slot');
          const currentDiscounts = document.querySelector('#sr-cart-discounts-slot');
          if (incomingDiscounts && currentDiscounts) currentDiscounts.innerHTML = incomingDiscounts.innerHTML;
          const incomingOffer = parsed.querySelector('#sr-cart-bogo-slot');
          const currentOffer = document.querySelector('#sr-cart-bogo-slot');
          if (incomingOffer && currentOffer) currentOffer.innerHTML = incomingOffer.innerHTML;
        }
        // Suppress entrance animation on re-rendered tickets so a single
        // qty change doesn't cause every card to "rise" again.
        current.querySelectorAll('.sr-ticket').forEach((el) => el.classList.add('no-anim'));
        return;
      }
    }
  };

  const refreshSections = async () => {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    // Section Rendering API needs the rendered template IDs, and an Accept: JSON
    // header makes /cart return cart JSON instead of section HTML.
    const itemsId = document.getElementById('main-cart-items')?.dataset.id;
    const footerId = document.getElementById('main-cart-footer')?.dataset.id;
    try {
      if (!itemsId || !footerId) throw new Error('Cart sections not found.');
      const response = await fetch(`${cartUrl}?sections=${itemsId},${footerId}`);
      if (!response.ok) throw new Error('Could not refresh cart sections.');
      const data = await response.json();
      replaceSection(ITEMS_SECTION_ID, data[itemsId]);
      replaceSection(FOOTER_SECTION_ID, data[footerId]);
    } catch (_e) {
      window.location.reload();
    }
  };

  const getCart = async () => {
    const response = await fetch(getAjaxUrl('cart_url', '/cart'), {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Could not load cart.');
    return response.json();
  };

  const showError = (line, message) => {
    const errorEl = document.getElementById(`Line-item-error-${line}`);
    if (errorEl) {
      const text = errorEl.querySelector('.cart-item__error-text');
      if (text) text.textContent = message || '';
    }
    const cartErrors = document.getElementById('cart-errors');
    if (cartErrors) cartErrors.textContent = message || '';
  };

  const announce = (message) => {
    const region = document.getElementById('cart-live-region-text');
    if (!region) return;
    region.textContent = message || '';
    region.setAttribute('aria-hidden', message ? 'false' : 'true');
  };

  const setBogoUpdating = (form, on) => {
    const submit = form.querySelector('[data-sr-bogo-submit]');
    const label = form.querySelector('[data-sr-bogo-submit-label]');
    form.setAttribute('aria-busy', on ? 'true' : 'false');
    if (submit) submit.disabled = !!on;
    if (label) label.textContent = on ? 'Adding ticket...' : 'Add my free ticket';
  };

  const showBogoStatus = (form, message) => {
    const status = form.querySelector('[data-sr-bogo-status]');
    if (status) status.textContent = message || '';
  };

  const claimBogo = async (form) => {
    const variant = form.querySelector('[name="id"]');
    if (!variant || form.getAttribute('aria-busy') === 'true') return;

    setBogoUpdating(form, true);
    showBogoStatus(form, '');

    try {
      const response = await fetch(getAjaxUrl('cart_add_url', '/cart/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variant.value), quantity: 1 }],
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        const message = (result && (result.description || result.message)) || 'Could not add the free ticket.';
        showBogoStatus(form, message);
        announce(message);
        setBogoUpdating(form, false);
        return;
      }

      const cart = await getCart();
      announce('Free ticket added. Your offer has been applied.');
      updateNavBadge(cart);
      updateCartStatus(cart);
      document.dispatchEvent(new CustomEvent('sr:cart-updated', { detail: { cart } }));
      await refreshSections();
    } catch (_e) {
      const message = 'Network error. Please try again.';
      showBogoStatus(form, message);
      announce(message);
      setBogoUpdating(form, false);
    }
  };

  const updateLine = async (line, quantity, { animateRemove = false } = {}) => {
    setItemUpdating(line, true);
    showError(line, '');

    const card = document.querySelector(`[data-sr-cart-line="${line}"]`);

    try {
      const response = await fetch(getAjaxUrl('cart_change_url', '/cart/change'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
        }),
      });

      const cart = await response.json();

      if (!response.ok) {
        const message = (cart && (cart.description || cart.message)) || 'Could not update cart.';
        showError(line, message);
        announce(message);
        setItemUpdating(line, false);
        return;
      }

      announce(quantity === 0 ? 'Pass removed.' : 'Quantity updated.');
      updateNavBadge(cart);
      updateCartStatus(cart);
      document.dispatchEvent(new CustomEvent('sr:cart-updated', { detail: { cart } }));

      if (animateRemove && quantity === 0 && card) {
        card.classList.add('is-removing');
        await new Promise((resolve) => setTimeout(resolve, 280));
      }

      // Cart emptied — let the page reload so the theatrical empty state renders.
      if (cart.item_count === 0) {
        window.location.assign((window.routes && window.routes.cart_url) || '/cart');
        return;
      }

      await refreshSections();
    } catch (_e) {
      showError(line, 'Network error. Refreshing.');
      window.setTimeout(() => window.location.reload(), 600);
    }
  };

  const onClick = (event) => {
    const remove = event.target.closest('[data-sr-cart-remove]');
    if (remove) {
      event.preventDefault();
      const line = remove.dataset.line;
      updateLine(line, 0, { animateRemove: true });
      return;
    }

    const stepper = event.target.closest('[data-sr-qty]');
    if (stepper) {
      event.preventDefault();
      const line = stepper.dataset.line;
      const input = document.getElementById(`Quantity-${line}`);
      if (!input) return;
      const direction = stepper.dataset.srQty === 'plus' ? 1 : -1;
      const next = Math.max(0, parseInt(input.value, 10) + direction);
      input.value = String(next);
      updateLine(line, next, { animateRemove: next === 0 });
    }
  };

  const onInputChange = (event) => {
    const input = event.target.closest('[data-sr-qty-input]');
    if (!input) return;
    const line = input.dataset.line;
    const next = Math.max(0, parseInt(input.value, 10) || 0);
    input.value = String(next);
    updateLine(line, next, { animateRemove: next === 0 });
  };

  const onSubmit = (event) => {
    const form = event.target.closest('[data-sr-bogo-form]');
    if (!form) return;
    event.preventDefault();
    claimBogo(form);
  };

  const init = () => {
    const root = document.querySelector('[data-sr-cart]');
    if (!root) return;
    if (root.dataset.srCartInit === 'true') return;
    root.dataset.srCartInit = 'true';

    root.addEventListener('click', onClick);
    root.addEventListener('change', onInputChange);
    root.addEventListener('submit', onSubmit);

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target.closest('[data-sr-qty-input]');
      if (!input) return;
      event.preventDefault();
      input.blur();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // re-init delegation if section is replaced (idempotent because flag-based listeners)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail && event.detail.sectionId &&
        (event.detail.sectionId.indexOf('cart') !== -1)) {
      init();
    }
  });
})();
